<template>
  <div class="tutorial-management-container">
    <div class="page-header">
      <h2>教程管理</h2>
      <el-button type="primary" @click="showAddTutorialDialog">
        <el-icon><Plus /></el-icon> 添加教程
      </el-button>
    </div>
    
    <!-- 教程列表 -->
    <el-table
      v-loading="loading"
      :data="tutorials"
      border
      style="width: 100%; margin-top: 20px;"
    >
      <el-table-column type="index" width="50" align="center" />
      
      <el-table-column label="封面" width="100">
        <template #default="scope">
          <el-image
            style="width: 60px; height: 40px;"
            :src="scope.row.cover_image || '/default-tutorial-cover.png'"
            fit="cover"
            :preview-src-list="scope.row.cover_image ? [scope.row.cover_image] : []"
            :initial-index="0"
          >
            <template #error>
              <div class="image-error">
                <el-icon><Picture /></el-icon>
              </div>
            </template>
          </el-image>
        </template>
      </el-table-column>
      
      <el-table-column prop="title" label="标题" min-width="120" />
      
      <el-table-column prop="description" label="简介" min-width="200">
        <template #default="scope">
          <el-tooltip
            v-if="scope.row.description && scope.row.description.length > 50"
            class="box-item"
            effect="dark"
            :content="scope.row.description"
            placement="top-start"
          >
            <span>{{ scope.row.description.substring(0, 50) }}...</span>
          </el-tooltip>
          <span v-else>{{ scope.row.description || '-' }}</span>
        </template>
      </el-table-column>
      
      <el-table-column prop="external_link" label="外部链接" min-width="150">
        <template #default="scope">
          <el-link
            v-if="scope.row.external_link"
            type="primary"
            :href="scope.row.external_link"
            target="_blank"
          >
            {{ scope.row.external_link.length > 30 ? scope.row.external_link.substring(0, 30) + '...' : scope.row.external_link }}
          </el-link>
          <span v-else>-</span>
        </template>
      </el-table-column>
      
      <el-table-column prop="views" label="浏览量" width="90" align="center" />
      
      <el-table-column prop="sort_order" label="排序" width="80" align="center" />
      
      <el-table-column prop="status" label="状态" width="100" align="center">
        <template #default="scope">
          <el-tag
            :type="scope.row.status === 'active' ? 'success' : 'info'"
            disable-transitions
          >
            {{ scope.row.status === 'active' ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column
        label="操作"
        width="180"
        fixed="right"
        align="center"
      >
        <template #default="scope">
          <el-button
            type="primary"
            size="small"
            @click="handleEditTutorial(scope.row)"
          >
            编辑
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="handleDeleteTutorial(scope.row)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 教程表单对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '编辑教程' : '添加教程'"
      width="70%"
      :before-close="handleDialogClose"
    >
      <el-form
        ref="formRef"
        :model="tutorialForm"
        :rules="rules"
        label-width="100px"
      >
        <el-form-item label="标题" prop="title">
          <el-input v-model="tutorialForm.title" placeholder="请输入教程标题" />
        </el-form-item>
        
        <el-form-item label="简介" prop="description">
          <el-input
            v-model="tutorialForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入教程简介"
          />
        </el-form-item>
        
        <el-form-item label="封面图片">
          <div class="upload-container">
            <el-upload
              class="avatar-uploader"
              :http-request="handleCoverUpload"
              :show-file-list="false"
              :before-upload="beforeCoverUpload"
              accept=".jpg,.jpeg,.png,.gif,.webp"
            >
              <img 
                v-if="tutorialForm.cover_image" 
                :src="tutorialForm.cover_image" 
                class="avatar" 
              />
              <el-icon v-else class="avatar-uploader-icon"><Plus /></el-icon>
            </el-upload>
            <div class="upload-tips">
              <p>支持 jpg, png, webp 格式，建议尺寸比例 16:9</p>
            </div>
          </div>
        </el-form-item>
        
        <el-form-item label="内容" prop="content">
          <div class="editor-container">
            <!-- 使用富文本编辑器 -->
            <RichTextEditor
              v-model="tutorialForm.content"
              height="300px"
              placeholder="请输入教程内容，支持图片和文字"
            />
          </div>
        </el-form-item>
        
        <el-form-item label="外部链接" prop="external_link">
          <el-input 
            v-model="tutorialForm.external_link" 
            placeholder="请输入外部教程链接，用户点击跳转"
          />
        </el-form-item>
        
        <el-form-item label="排序" prop="sort_order">
          <el-input-number 
            v-model="tutorialForm.sort_order" 
            :min="0" 
            :max="999"
            placeholder="排序值，数字越小排序越靠前"
          />
        </el-form-item>
        
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="tutorialForm.status">
            <el-radio label="active">启用</el-radio>
            <el-radio label="inactive">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        
        <el-form-item>
          <el-button type="primary" @click="submitForm" :loading="submitting">
            {{ isEdit ? '保存修改' : '添加教程' }}
          </el-button>
          <el-button @click="handleDialogClose">取消</el-button>
        </el-form-item>
      </el-form>
    </el-dialog>
    
    <!-- 删除确认对话框 -->
    <el-dialog
      v-model="deleteDialogVisible"
      title="确认删除"
      width="30%"
      center
    >
      <p>确定要删除教程 "{{ selectedTutorial?.title }}" 吗？此操作无法恢复。</p>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="deleteDialogVisible = false">取消</el-button>
          <el-button 
            type="danger" 
            @click="confirmDelete" 
            :loading="deleting"
          >
            确认删除
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, Picture } from '@element-plus/icons-vue';
import { 
  fetchAdminTutorials, 
  uploadTutorialImage, 
  addTutorial, 
  updateTutorial, 
  deleteTutorial,
  fetchAdminTutorialById
} from '@/api/tutorial';
import RichTextEditor from '@/components/editor/RichTextEditor.vue';

// 教程列表
const tutorials = ref([]);
const loading = ref(true);

// 表单相关
const formRef = ref(null);
const dialogVisible = ref(false);
const isEdit = ref(false);
const submitting = ref(false);
const tutorialForm = reactive({
  id: null,
  title: '',
  description: '',
  cover_image: '',
  content: '',
  external_link: '',
  sort_order: 0,
  status: 'active'
});

// 表单验证规则
const rules = {
  title: [
    { required: true, message: '请输入教程标题', trigger: 'blur' },
    { max: 100, message: '标题长度不能超过100个字符', trigger: 'blur' }
  ],
  description: [
    { max: 500, message: '简介长度不能超过500个字符', trigger: 'blur' }
  ],
  external_link: [
    { 
      pattern: /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w-./?%&=_~+:#]*)?$/, 
      message: '请输入有效的URL', 
      trigger: 'blur' 
    }
  ]
};

// 删除相关
const deleteDialogVisible = ref(false);
const deleting = ref(false);
const selectedTutorial = ref(null);

// 获取教程列表
const fetchTutorialList = async () => {
  loading.value = true;
  
  try {
    const data = await fetchAdminTutorials();
    tutorials.value = data;
  } catch (err) {
    console.error('获取教程列表失败:', err);
    ElMessage.error('获取教程列表失败，请稍后再试');
  } finally {
    loading.value = false;
  }
};

// 显示添加教程对话框
const showAddTutorialDialog = () => {
  isEdit.value = false;
  resetForm();
  dialogVisible.value = true;
};

// 处理编辑教程
const handleEditTutorial = (tutorial) => {
  isEdit.value = true;
  
  // 复制对象，避免直接修改原始数据
  tutorialForm.id = tutorial.id;
  tutorialForm.title = tutorial.title;
  tutorialForm.description = tutorial.description || '';
  tutorialForm.cover_image = tutorial.cover_image || '';
  tutorialForm.content = tutorial.content || '';
  tutorialForm.external_link = tutorial.external_link || '';
  tutorialForm.sort_order = tutorial.sort_order || 0;
  tutorialForm.status = tutorial.status || 'active';
  
  // 如果content字段不存在或为空，尝试从服务器获取完整的教程信息
  if (!tutorial.content) {
    fetchTutorialDetails(tutorial.id);
  }
  
  dialogVisible.value = true;
};

// 获取单个完整的教程数据
const fetchTutorialDetails = async (id) => {
  try {
    const data = await fetchAdminTutorialById(id);
    if (data && data.content) {
      tutorialForm.content = data.content;
      ElMessage.success('教程内容加载成功');
    }
  } catch (err) {
    console.error('获取教程详情失败:', err);
    ElMessage.warning('获取教程详情内容失败，可能需要重新编辑内容');
  }
};

// 处理删除教程
const handleDeleteTutorial = (tutorial) => {
  selectedTutorial.value = tutorial;
  deleteDialogVisible.value = true;
};

// 确认删除教程
const confirmDelete = async () => {
  if (!selectedTutorial.value) return;
  
  deleting.value = true;
  
  try {
    await deleteTutorial(selectedTutorial.value.id);
    ElMessage.success('教程删除成功');
    fetchTutorialList();
  } catch (err) {
    console.error('删除教程失败:', err);
    ElMessage.error('删除教程失败，请稍后再试');
  } finally {
    deleting.value = false;
    deleteDialogVisible.value = false;
    selectedTutorial.value = null;
  }
};

// 封面图片上传前验证
const beforeCoverUpload = (file) => {
  const isImage = /image\/(jpeg|png|gif|jpg|webp)/.test(file.type);
  const isLt5M = file.size / 1024 / 1024 < 5;
  
  if (!isImage) {
    ElMessage.error('请上传图片文件!');
    return false;
  }
  
  if (!isLt5M) {
    ElMessage.error('图片大小不能超过5MB!');
    return false;
  }
  
  return true;
};

// 处理封面图片上传
const handleCoverUpload = async ({ file }) => {
  try {
    const response = await uploadTutorialImage(file);
    
    if (response && response.url) {
      tutorialForm.cover_image = response.url;
      ElMessage.success('封面图片上传成功');
    } else {
      ElMessage.error('封面图片上传失败');
    }
  } catch (err) {
    console.error('封面图片上传失败:', err);
    ElMessage.error('封面图片上传失败，请稍后再试');
  }
};

// 提交表单
const submitForm = async () => {
  if (!formRef.value) return;
  
  formRef.value.validate(async (valid) => {
    if (!valid) {
      return false;
    }
    
    submitting.value = true;
    
    try {
      const formData = {
        title: tutorialForm.title,
        description: tutorialForm.description,
        cover_image: tutorialForm.cover_image,
        content: tutorialForm.content,
        external_link: tutorialForm.external_link,
        sort_order: tutorialForm.sort_order,
        status: tutorialForm.status
      };
      
      if (isEdit.value) {
        // 更新教程
        await updateTutorial(tutorialForm.id, formData);
        ElMessage.success('教程更新成功');
      } else {
        // 添加教程
        await addTutorial(formData);
        ElMessage.success('教程添加成功');
      }
      
      dialogVisible.value = false;
      fetchTutorialList();
    } catch (err) {
      console.error('提交教程失败:', err);
      ElMessage.error('提交教程失败，请稍后再试');
    } finally {
      submitting.value = false;
    }
  });
};

// 重置表单
const resetForm = () => {
  tutorialForm.id = null;
  tutorialForm.title = '';
  tutorialForm.description = '';
  tutorialForm.cover_image = '';
  tutorialForm.content = '';
  tutorialForm.external_link = '';
  tutorialForm.sort_order = 0;
  tutorialForm.status = 'active';
  
  // 如果表单引用存在，重置验证
  if (formRef.value) {
    formRef.value.resetFields();
  }
};

// 处理对话框关闭
const handleDialogClose = () => {
  ElMessageBox.confirm(
    '确认关闭？未保存的更改将丢失',
    '警告',
    {
      confirmButtonText: '确认',
      cancelButtonText: '取消',
      type: 'warning'
    }
  )
    .then(() => {
      dialogVisible.value = false;
      resetForm();
    })
    .catch(() => {
      // 用户取消关闭
    });
};

onMounted(() => {
  fetchTutorialList();
});
</script>

<style scoped>
.tutorial-management-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.upload-container {
  display: flex;
  align-items: flex-start;
}

.avatar-uploader {
  width: 178px;
  height: 100px;
  border: 1px dashed var(--el-border-color);
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: var(--el-transition-duration-fast);
}

.avatar-uploader:hover {
  border-color: var(--el-color-primary);
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.avatar {
  width: 178px;
  height: 100px;
  display: block;
  object-fit: cover;
}

.upload-tips {
  margin-left: 16px;
  color: #909399;
  font-size: 12px;
}

.editor-container {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.image-error {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 20px;
}
</style> 